import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SetupWizardContext } from "../../context/SetupWizardContext";
import { useTour } from "../../context/TourContext";
import { getRequest } from "../../Apis/apiWrapper";
import {
  authEndpoints,
  CampaignSettingsEndpoints,
  icpEndpoints,
} from "../../utils/constants/httpConstants";
import { PROFILE_PATH } from "../../utils/constants/routes";
import { getTourProgressAction } from "../../redux/actions/guidedTourActions";
import SetupWizard from "./SetupWizard";

// ── Step definitions ──────────────────────────────────────────────────────────
// Exported so SetupWizard.js can reference them without re-declaring.
export const SETUP_STEPS = [
  {
    key: "mailbox",
    label: "Mailbox Connection",
    tabId: "Integrations",
    title: "Connect Your Mailbox",
    description:
      "Emails in your campaigns are sent from your connected mailbox. Without one, outreach cannot begin. Gmail, Outlook, and several other providers are supported, so connect whichever works best for you.",
    notDoneHint:
      "We couldn't detect a connected mailbox yet. Please connect one and try again.",
  },
  {
    key: "signature",
    label: "Email Signature",
    tabId: "CampaignDefaults",
    title: "Create an Email Signature",
    description:
      "Your signature appears on every email you send, letting recipients know who you are and giving your outreach a polished, professional look.",
    notDoneHint:
      "We couldn't find an email signature yet. Please create one and try again.",
  },
  {
    key: "icp",
    label: "ICP Profile",
    tabId: "ICPSettings",
    title: "Set Up Your ICP",
    description:
      "Your Ideal Customer Profile (ICP) defines exactly who you want to reach, using criteria like industry, company size, seniority, and much more. You can also set dealbreakers, which are instant red flags. Any prospect carrying even one is filtered out immediately, keeping your pipeline clean and your outreach focused.",
    notDoneHint:
      "We couldn't find an ICP yet. Please create one and try again.",
  },
  {
    key: "kb",
    label: "Knowledge Base",
    tabId: "Memory",
    title: "Fill Your Knowledge Base",
    description:
      "The Knowledge Base is where all your company context lives. Add your company details, product info, who you help, the problems you solve, and more. ReachIQ uses this context to make the outreach it generates for your campaigns accurate, specific, and highly relevant to each prospect.",
    notDoneHint:
      "We couldn't find any content in your Knowledge Base yet. Please add some details and try again.",
  },
];

const SESSION_KEY = "__riq_setup_wizard";

// ── Status checkers ───────────────────────────────────────────────────────────
const checkMailbox = async () => {
  try {
    const res = await getRequest(null, authEndpoints.getApiKeyDetails);
    const inboxes = res?.data?.data?.inboxes || {};
    const hasInbox = Object.values(inboxes).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    const hasOtherEsp =
      Array.isArray(res?.data?.data?.other_api_details) &&
      res.data.data.other_api_details.length > 0;
    return hasInbox || hasOtherEsp;
  } catch {
    return false;
  }
};

const checkSignature = async () => {
  try {
    const res = await getRequest(null, CampaignSettingsEndpoints.signatureList);
    const data = res?.data?.data || {};
    return Object.values(data).some(
      (bucket) =>
        Array.isArray(bucket?.signatures) &&
        bucket.signatures.some((s) => s?.is_default === true)
    );
  } catch {
    return false;
  }
};

const checkICP = async () => {
  try {
    const res = await getRequest(
      null,
      `${icpEndpoints.getAllPersonas}?_t=${Date.now()}`
    );
    return Array.isArray(res?.data?.data) && res.data.data.length > 0;
  } catch {
    return false;
  }
};

const checkKB = async () => {
  try {
    const res = await getRequest(null, authEndpoints.getCompanySummary);
    return !!res?.data?.data?.summary;
  } catch {
    return false;
  }
};

const STATUS_CHECKERS = {
  mailbox: checkMailbox,
  signature: checkSignature,
  icp: checkICP,
  kb: checkKB,
};

const checkAllStatuses = async () => {
  const [mailbox, signature, icp, kb] = await Promise.all([
    checkMailbox(),
    checkSignature(),
    checkICP(),
    checkKB(),
  ]);
  return { mailbox, signature, icp, kb };
};

// ── Provider ──────────────────────────────────────────────────────────────────
export default function SetupWizardProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { startPipeline } = useTour();

  // Wizard phase drives what the UI renders
  // 'idle'     → nothing shown
  // 'checking' → fetching initial statuses (no UI yet)
  // 'intro'    → centered welcome modal (welcome source only)
  // 'step'     → step card (centered or minimized)
  // 'outro'    → centered completion modal (welcome source only)
  const [wizardPhase, setWizardPhase] = useState("idle");
  const [currentStepKey, setCurrentStepKey] = useState(null);
  // Snapshot of pending keys when wizard started — used for progress bar, never changes
  const [initialPendingKeys, setInitialPendingKeys] = useState([]);
  // Remaining pending keys (shrinks as steps are confirmed/skipped)
  const [pendingStepKeys, setPendingStepKeys] = useState([]);
  // Full status of all 4 items (including pre-done ones)
  const [stepsStatus, setStepsStatus] = useState({
    mailbox: false,
    signature: false,
    icp: false,
    kb: false,
  });
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const [launchSource, setLaunchSource] = useState(null); // 'welcome' | 'learning_center'
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null); // null | 'notDone' | 'error'
  // 'detecting' = waiting for KB to be created; 'ready' = KB exists but user clicked Add More Sources
  const [kbDetectionState, setKbDetectionState] = useState("detecting");
  // 'detecting' = waiting for ICP persona to be created; 'continuing' = auto-advance in progress
  const [icpDetectionState, setIcpDetectionState] = useState("detecting");
  // 'detecting' = waiting for a mailbox to be connected; 'continuing' = auto-advance in progress
  const [mailboxDetectionState, setMailboxDetectionState] = useState("detecting");
  // 'detecting' = waiting for a signature to be created; 'continuing' = auto-advance in progress
  const [signatureDetectionState, setSignatureDetectionState] = useState("detecting");

  // Callback fired after setup wizard completes (only for 'welcome' source)
  const onCompleteRef = useRef(null);

  // Refs for stable access inside event listener closures (avoids stale state)
  const currentStepKeyRef = useRef(currentStepKey);
  useEffect(() => { currentStepKeyRef.current = currentStepKey; }, [currentStepKey]);
  const wizardPhaseRef = useRef(wizardPhase);
  useEffect(() => { wizardPhaseRef.current = wizardPhase; }, [wizardPhase]);

  const wizardActive = wizardPhase !== "idle" && wizardPhase !== "checking";

  // Stable ref so the KB event listener (set up once) always calls the latest confirmStepDone
  const confirmStepDoneRef = useRef(null);
  // Stable ref for direct advance (mailbox/ICP — condition pre-verified at dispatch time)
  const _advanceCurrentStepRef = useRef(null);

  // ── Persist to sessionStorage so wizard survives OAuth same-tab redirects ──
  // sessionStorage persists through same-tab page redirects (e.g. Gmail OAuth)
  // but is cleared when the tab is closed — intentional: stale wizard state
  // would be confusing after a fresh login. Learning Center checklist lets
  // users resume incomplete setup at any time without wizard state.
  useEffect(() => {
    if (wizardPhase === "step" || wizardPhase === "intro") {
      try {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            phase: wizardPhase,
            currentStepKey,
            initialPendingKeys,
            pendingStepKeys,
            stepsStatus,
            skippedSteps,
            launchSource,
            minimized,
            shouldStartTours: launchSource === "welcome",
          })
        );
      } catch { /* quota errors are non-fatal */ }
    }
  }, [wizardPhase, currentStepKey, initialPendingKeys, pendingStepKeys, stepsStatus, skippedSteps, launchSource, minimized]);

  // ── Restore from sessionStorage on mount (OAuth redirect return) ───────────
  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }
    if (!saved?.currentStepKey) {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }

    // Re-check only the interrupted step — it may have been completed during OAuth
    STATUS_CHECKERS[saved.currentStepKey]?.().then((isDone) => {
      const newStatus = {
        ...(saved.stepsStatus || {}),
        ...(isDone ? { [saved.currentStepKey]: true } : {}),
      };
      const remaining = (saved.pendingStepKeys || []).filter(
        (k) => k !== saved.currentStepKey || !isDone
      );

      if (saved.shouldStartTours) {
        onCompleteRef.current = _buildTourLauncher_standalone(dispatch, navigate, startPipeline);
      }

      if (remaining.length === 0) {
        sessionStorage.removeItem(SESSION_KEY);
        // Learning Center launches don't show outro — just dismiss silently on refresh.
        if ((saved.launchSource || "welcome") !== "learning_center") {
          setStepsStatus(newStatus);
          setInitialPendingKeys(saved.initialPendingKeys || []);
          setPendingStepKeys([]);
          setSkippedSteps(saved.skippedSteps || []);
          setLaunchSource(saved.launchSource || "welcome");
          setWizardPhase("outro");
        }
        return;
      }

      const nextKey = isDone ? remaining[0] : saved.currentStepKey;
      const nextStep = SETUP_STEPS.find((s) => s.key === nextKey);
      setStepsStatus(newStatus);
      setInitialPendingKeys(saved.initialPendingKeys || []);
      setPendingStepKeys(remaining);
      setSkippedSteps(saved.skippedSteps || []);
      setLaunchSource(saved.launchSource || "welcome");
      setCurrentStepKey(nextKey);
      setMinimized(saved.minimized ?? false);
      setWizardPhase("step");
      if (nextStep) {
        const search = nextStep.extraSearch ? `?tab=${nextStep.tabId}&${nextStep.extraSearch}` : `?tab=${nextStep.tabId}`;
        navigate(`${PROFILE_PATH}${search}`);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset detection states when leaving their respective steps ───────────
  useEffect(() => {
    if (currentStepKey !== "kb") setKbDetectionState("detecting");
    if (currentStepKey !== "icp") setIcpDetectionState("detecting");
    if (currentStepKey !== "mailbox") setMailboxDetectionState("detecting");
    if (currentStepKey !== "signature") setSignatureDetectionState("detecting");
  }, [currentStepKey]);

  // ── KB auto-detection event listeners ─────────────────────────────────────
  // Fired from MemoryScreen/MemoryPreviewModal when KB-related actions complete.
  useEffect(() => {
    const isKBStep = () =>
      wizardPhaseRef.current === "step" && currentStepKeyRef.current === "kb";

    const handleAutoAdvance = () => {
      if (!isKBStep()) return;
      const stepKeyAtFire = currentStepKeyRef.current;
      setMinimized(false);
      setKbDetectionState("continuing");
      setTimeout(() => {
        if (
          currentStepKeyRef.current === stepKeyAtFire &&
          wizardPhaseRef.current === "step"
        ) {
          _advanceCurrentStepRef.current?.();
        }
      }, 3000);
    };
    const handleAddMoreSources = () => {
      if (isKBStep()) setKbDetectionState("ready");
    };
    const handleDeleted = () => {
      if (isKBStep()) setKbDetectionState("detecting");
    };

    window.addEventListener("setup:kb-auto-advance", handleAutoAdvance);
    window.addEventListener("setup:kb-add-more-sources", handleAddMoreSources);
    window.addEventListener("setup:kb-deleted", handleDeleted);
    return () => {
      window.removeEventListener("setup:kb-auto-advance", handleAutoAdvance);
      window.removeEventListener("setup:kb-add-more-sources", handleAddMoreSources);
      window.removeEventListener("setup:kb-deleted", handleDeleted);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ICP auto-detection event listeners ────────────────────────────────────
  // Fired from ICPSettingsScreen when a new persona is created successfully.
  useEffect(() => {
    const isICPStep = () =>
      wizardPhaseRef.current === "step" && currentStepKeyRef.current === "icp";

    const handleIcpAutoAdvance = () => {
      if (!isICPStep()) return;
      const stepKeyAtFire = currentStepKeyRef.current;
      setMinimized(false);
      setIcpDetectionState("continuing");
      setTimeout(() => {
        if (
          currentStepKeyRef.current === stepKeyAtFire &&
          wizardPhaseRef.current === "step"
        ) {
          _advanceCurrentStepRef.current?.();
        }
      }, 3000);
    };

    window.addEventListener("setup:icp-auto-advance", handleIcpAutoAdvance);
    return () => {
      window.removeEventListener("setup:icp-auto-advance", handleIcpAutoAdvance);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mailbox auto-detection event listeners ────────────────────────────────
  // Fired from ConnectedEspList when get-api-details returns at least one connected inbox.
  useEffect(() => {
    const isMailboxStep = () =>
      wizardPhaseRef.current === "step" && currentStepKeyRef.current === "mailbox";

    const handleMailboxAutoAdvance = () => {
      if (!isMailboxStep()) return;
      const stepKeyAtFire = currentStepKeyRef.current;
      setMinimized(false);
      setMailboxDetectionState("continuing");
      setTimeout(() => {
        if (
          currentStepKeyRef.current === stepKeyAtFire &&
          wizardPhaseRef.current === "step"
        ) {
          _advanceCurrentStepRef.current?.();
        }
      }, 3000);
    };

    window.addEventListener("setup:mailbox-auto-advance", handleMailboxAutoAdvance);
    return () => {
      window.removeEventListener("setup:mailbox-auto-advance", handleMailboxAutoAdvance);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Signature auto-detection event listener ───────────────────────────────
  // Fired from SignatureFormModal when a new signature is successfully created.
  useEffect(() => {
    const isSignatureStep = () =>
      wizardPhaseRef.current === "step" && currentStepKeyRef.current === "signature";

    const handleSignatureAutoAdvance = () => {
      if (!isSignatureStep()) return;
      const stepKeyAtFire = currentStepKeyRef.current;
      setMinimized(false);
      setSignatureDetectionState("continuing");
      setTimeout(() => {
        if (
          currentStepKeyRef.current === stepKeyAtFire &&
          wizardPhaseRef.current === "step"
        ) {
          _advanceCurrentStepRef.current?.();
        }
      }, 3000);
    };

    window.addEventListener("setup:signature-auto-advance", handleSignatureAutoAdvance);
    return () => {
      window.removeEventListener("setup:signature-auto-advance", handleSignatureAutoAdvance);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Route guard — auto-dismiss if user leaves /user/settings ───────────────
  useEffect(() => {
    if (!wizardActive) return;
    if (wizardPhase === "outro") return; // outro can render anywhere
    if (!location.pathname.startsWith(PROFILE_PATH)) {
      _resetWizard();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────
  const _resetWizard = useCallback(() => {
    setWizardPhase("idle");
    setCurrentStepKey(null);
    setInitialPendingKeys([]);
    setPendingStepKeys([]);
    setStepsStatus({ mailbox: false, signature: false, icp: false, kb: false });
    setSkippedSteps([]);
    setMinimized(false);
    setLaunchSource(null);
    setVerifying(false);
    setVerifyError(null);
    setKbDetectionState("detecting");
    setIcpDetectionState("detecting");
    setMailboxDetectionState("detecting");
    setSignatureDetectionState("detecting");
    onCompleteRef.current = null;
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  const _buildTourLauncher = useCallback(() => {
    return async () => {
      try {
        const result = await dispatch(getTourProgressAction(navigate));
        if (result?.success) {
          const tours = result.data?.data?.tours ?? [];
          const pipeline = [...tours]
            .sort((a, b) => a.order - b.order)
            .flatMap((t) =>
              t.sub_tours?.length
                ? [...t.sub_tours]
                    .sort((a, b) => a.order - b.order)
                    .map((s) => ({ key: s.key, fromStep: 0, name: s.name }))
                : [{ key: t.key, fromStep: 0, name: t.name }]
            );
          startPipeline(pipeline);
        }
      } catch { /* silent — user can start tours from Learning Center */ }
    };
  }, [dispatch, navigate, startPipeline]);

  const STEP_TOUR_ATTR = {
    mailbox: "integrations-tab",
    signature: "email-signatures-tab",
    icp: "icp-settings-tab",
    kb: "memory-tab",
  };

  const _navigateToStep = useCallback((key) => {
    const step = SETUP_STEPS.find((s) => s.key === key);
    if (!step) return;
    const search = step.extraSearch ? `?tab=${step.tabId}&${step.extraSearch}` : `?tab=${step.tabId}`;
    navigate(`${PROFILE_PATH}${search}`);
    const tourAttr = STEP_TOUR_ATTR[key];
    if (tourAttr) {
      setTimeout(() => {
        document
          .querySelector(`[data-tour="${tourAttr}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 150);
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Public API ────────────────────────────────────────────────────────────

  // Called by WelcomeModal "Start Tour" button
  const startWizardFromWelcome = useCallback(async () => {
    setWizardPhase("checking");
    setLaunchSource("welcome");
    onCompleteRef.current = _buildTourLauncher();

    try {
      const status = await checkAllStatuses();
      const pending = SETUP_STEPS.filter((s) => !status[s.key]).map((s) => s.key);

      if (pending.length === 0) {
        // Everything is already set up — go straight to tours
        setWizardPhase("idle");
        await onCompleteRef.current?.();
        onCompleteRef.current = null;
        return;
      }

      setStepsStatus(status);
      setInitialPendingKeys(pending);
      setPendingStepKeys(pending);
      setCurrentStepKey(pending[0]);
      setSkippedSteps([]);
      setMinimized(false);
      setWizardPhase("intro");
      navigate(`${PROFILE_PATH}?tab=General`);
    } catch {
      // On error just fall through to tours
      setWizardPhase("idle");
      await onCompleteRef.current?.();
      onCompleteRef.current = null;
    }
  }, [_buildTourLauncher, navigate]);

  // Called from Learning Center "Set up →" for a single step
  const startWizardFromLearningCenter = useCallback((stepKey) => {
    setLaunchSource("learning_center");
    setCurrentStepKey(stepKey);
    setInitialPendingKeys([stepKey]);
    setPendingStepKeys([stepKey]);
    setSkippedSteps([]);
    setMinimized(false);
    setVerifying(false);
    setVerifyError(null);
    setWizardPhase("step");
    _navigateToStep(stepKey);
  }, [_navigateToStep]);

  // "Let's start" on intro modal
  const proceedFromIntro = useCallback(() => {
    if (pendingStepKeys.length === 0) {
      setWizardPhase("outro");
      return;
    }
    setCurrentStepKey(pendingStepKeys[0]);
    setWizardPhase("step");
    setMinimized(false);
    _navigateToStep(pendingStepKeys[0]);
  }, [pendingStepKeys, _navigateToStep]);

  // "Skip setup, go to tour" on intro modal
  const skipSetupFromIntro = useCallback(() => {
    setSkippedSteps(pendingStepKeys);
    setPendingStepKeys([]);
    setWizardPhase("outro");
  }, [pendingStepKeys]);

  // "I'm Done, Continue →" on step card
  const confirmStepDone = useCallback(async () => {
    if (!currentStepKey) return;

    // KB is already confirmed present (user saw "Continue" because kbDetectionState = "ready").
    // No need to hit the API again — advance directly.
    if (currentStepKey === "kb" && kbDetectionState === "ready") {
      _advanceCurrentStepRef.current?.();
      return;
    }

    setVerifying(true);
    setVerifyError(null);
    try {
      const isDone = await STATUS_CHECKERS[currentStepKey]?.();
      if (isDone) {
        const newStatus = { ...stepsStatus, [currentStepKey]: true };
        setStepsStatus(newStatus);
        const remaining = pendingStepKeys.filter((k) => k !== currentStepKey);
        setPendingStepKeys(remaining);
        setVerifying(false);
        setVerifyError(null);

        if (remaining.length > 0) {
          const nextKey = remaining[0];
          setCurrentStepKey(nextKey);
          setMinimized(false);
          _navigateToStep(nextKey);
        } else {
          if (launchSource === "learning_center") {
            _resetWizard();
          } else {
            setWizardPhase("outro");
          }
        }
      } else {
        setMailboxDetectionState("detecting");
        setIcpDetectionState("detecting");
        setKbDetectionState("detecting");
        setSignatureDetectionState("detecting");
        setVerifyError("notDone");
        setVerifying(false);
      }
    } catch {
      setMailboxDetectionState("detecting");
      setIcpDetectionState("detecting");
      setKbDetectionState("detecting");
      setSignatureDetectionState("detecting");
      setVerifyError("error");
      setVerifying(false);
    }
  }, [currentStepKey, kbDetectionState, stepsStatus, pendingStepKeys, launchSource, _resetWizard, _navigateToStep]);
  // Keep the ref current so the KB event listener always calls the latest version
  useEffect(() => { confirmStepDoneRef.current = confirmStepDone; }, [confirmStepDone]);

  // Direct advance — skips the API re-check because the condition is pre-verified at
  // dispatch time (mailbox: GET returned connected inboxes; ICP: POST succeeded).
  const _advanceCurrentStep = useCallback(() => {
    if (!currentStepKey) return;
    const newStatus = { ...stepsStatus, [currentStepKey]: true };
    setStepsStatus(newStatus);
    const remaining = pendingStepKeys.filter((k) => k !== currentStepKey);
    setPendingStepKeys(remaining);
    setVerifyError(null);

    if (remaining.length > 0) {
      const nextKey = remaining[0];
      setCurrentStepKey(nextKey);
      setMinimized(false);
      _navigateToStep(nextKey);
    } else {
      if (launchSource === "learning_center") {
        _resetWizard();
      } else {
        setWizardPhase("outro");
      }
    }
  }, [currentStepKey, stepsStatus, pendingStepKeys, launchSource, _resetWizard, _navigateToStep]);
  useEffect(() => { _advanceCurrentStepRef.current = _advanceCurrentStep; }, [_advanceCurrentStep]);

  // "Skip this step"
  const skipCurrentStep = useCallback(() => {
    if (!currentStepKey) return;
    setSkippedSteps((prev) => [...prev, currentStepKey]);
    const remaining = pendingStepKeys.filter((k) => k !== currentStepKey);
    setPendingStepKeys(remaining);
    setVerifyError(null);

    if (remaining.length > 0) {
      const nextKey = remaining[0];
      setCurrentStepKey(nextKey);
      setMinimized(false);
      _navigateToStep(nextKey);
    } else {
      if (launchSource === "learning_center") {
        _resetWizard();
      } else {
        setWizardPhase("outro");
      }
    }
  }, [currentStepKey, pendingStepKeys, launchSource, _resetWizard, _navigateToStep]);

  // "Skip all setup"
  const skipAllSetup = useCallback(() => {
    setSkippedSteps((prev) => [
      ...new Set([...prev, ...pendingStepKeys]),
    ]);
    setPendingStepKeys([]);
    if (launchSource === "learning_center") {
      _resetWizard();
    } else {
      setWizardPhase("outro");
    }
  }, [pendingStepKeys, launchSource, _resetWizard]);

  // "Continue" on outro modal
  const proceedFromOutro = useCallback(async () => {
    const cb = onCompleteRef.current;
    if (cb) await cb();
    _resetWizard();
  }, [_resetWizard]);

  const dismissWizard = useCallback(() => _resetWizard(), [_resetWizard]);
  const minimizeWizard = useCallback(() => setMinimized(true), []);
  const maximizeWizard = useCallback(() => { setMinimized(false); setVerifyError(null); }, []);

  return (
    <SetupWizardContext.Provider
      value={{
        wizardActive,
        wizardPhase,
        currentStepKey,
        initialPendingKeys,
        pendingStepKeys,
        stepsStatus,
        skippedSteps,
        minimized,
        launchSource,
        verifying,
        verifyError,
        kbDetectionState,
        icpDetectionState,
        mailboxDetectionState,
        signatureDetectionState,
        startWizardFromWelcome,
        startWizardFromLearningCenter,
        dismissWizard,
        minimizeWizard,
        maximizeWizard,
        confirmStepDone,
        skipCurrentStep,
        skipAllSetup,
        skipSetupFromIntro,
        proceedFromIntro,
        proceedFromOutro,
      }}
    >
      {children}
      <SetupWizard />
    </SetupWizardContext.Provider>
  );
}

// Standalone version of the tour launcher used when reconstructing from sessionStorage
// (we can't use the hook-based version outside a component, so we pass deps explicitly)
function _buildTourLauncher_standalone(dispatch, navigate, startPipeline) {
  return async () => {
    try {
      const result = await dispatch(getTourProgressAction(navigate));
      if (result?.success) {
        const tours = result.data?.data?.tours ?? [];
        const pipeline = [...tours]
          .sort((a, b) => a.order - b.order)
          .flatMap((t) =>
            t.sub_tours?.length
              ? [...t.sub_tours]
                  .sort((a, b) => a.order - b.order)
                  .map((s) => ({ key: s.key, fromStep: 0, name: s.name }))
              : [{ key: t.key, fromStep: 0, name: t.name }]
          );
        startPipeline(pipeline);
      }
    } catch { /* silent */ }
  };
}
